import type { Band, Confidence } from '@/api/types'

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral'

const variantClasses: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  error: 'bg-error/10 text-error dark:bg-error/20 dark:text-red-300',
  info: 'bg-secondary/20 text-primary dark:bg-secondary/15 dark:text-secondary',
  neutral: 'bg-container-light text-subtle-light dark:bg-subtle-dark/15 dark:text-subtle-dark',
}

export function Badge({ label, variant = 'neutral' }: { label: string; variant?: BadgeVariant }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${variantClasses[variant]}`}>
      {label}
    </span>
  )
}

// ── Risk band ─────────────────────────────────────────────────────────────────
// critical/high → error/warning tints; medium → info; low/info → neutral/success.

const BAND_CLASSES: Record<Band, string> = {
  critical: 'bg-error text-white dark:bg-error dark:text-white',
  high: 'bg-error/15 text-error dark:bg-error/25 dark:text-red-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  low: 'bg-secondary/20 text-primary dark:bg-secondary/15 dark:text-secondary',
  info: 'bg-container-light text-subtle-light dark:bg-subtle-dark/15 dark:text-subtle-dark',
}

export function BandBadge({ band }: { band: Band | string }) {
  const cls = BAND_CLASSES[band as Band] ?? BAND_CLASSES.info
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold uppercase tracking-wide rounded-full ${cls}`}
    >
      {band}
    </span>
  )
}

export function ConfidenceBadge({ confidence }: { confidence: Confidence | string }) {
  const map: Record<string, BadgeVariant> = {
    high: 'success',
    medium: 'warning',
    low: 'neutral',
  }
  return <Badge label={`${confidence} confidence`} variant={map[confidence] ?? 'neutral'} />
}
